import cn from 'clsx';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import PropTypes from 'prop-types';
import {memo, useEffect, useMemo, useState} from 'react';

import {SEARCH_FILTERS} from '@/src/app/constants/search';
import getLastDirectoryFromUrl from '@/src/app/utils/getLastDirectoryFromUrl';
import useDebounce from '@/src/hooks/useDebounce';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {SEARCH_TYPE} from '../../routes/containers/Routes';
import {Text} from '../../ui';
import css from '../styles/SearchFieldContainer.module.css';
import {useSearchFilters} from '../utils/useSearchFilters';

const SearchFieldContainer = ({withBottomMargin, searchLabel, withLabel}) => {
  const {t} = useTranslation('common');
  const {query, asPath} = useRouter();
  // @ts-ignore
  const {setQueryFilters} = useSearchFilters();
  const [searchValue, setSearchValue] = useState(
    query[SEARCH_FILTERS.SEARCH] || ''
  );
  const debouncedSearchTerm = useDebounce(searchValue, 700);
  const initialSearchValue = query[SEARCH_FILTERS.SEARCH] || '';

  const searchHeaderText = useMemo(() => {
    const isSearchForTargetUserCourses = Boolean(
      query[SEARCH_FILTERS.FIRST_NAME]
    );

    if (isSearchForTargetUserCourses) {
      return 'search.title-courses-target-user';
    }

    if (getLastDirectoryFromUrl(asPath) === SEARCH_TYPE.CLINICS) {
      return 'search.title-clinics';
    }

    if (getLastDirectoryFromUrl(asPath) === SEARCH_TYPE.POSTS) {
      return 'search.title-posts';
    }

    return getLastDirectoryFromUrl(asPath) === SEARCH_TYPE.COURSES
      ? 'search.title-courses'
      : 'search.title-users';
  }, [asPath, query]);

  useEffect(() => {
    if (!query[SEARCH_FILTERS.SEARCH]) {
      setSearchValue('');
    } else {
      setSearchValue(query[SEARCH_FILTERS.SEARCH]);
    }
  }, [asPath, query[SEARCH_FILTERS.SEARCH]]);

  useEffect(() => {
    if (debouncedSearchTerm !== initialSearchValue) {
      setQueryFilters({
        [SEARCH_FILTERS.SEARCH]: searchValue,
        [SEARCH_FILTERS.OFFSET]: '0',
      });
    }
  }, [debouncedSearchTerm]);

  const onSearchInputChangeHandler = (event) => {
    //eslint-disable-next-line
    const searchInoutRegex = /[\]\[\}\{!\\<\|>,\"#&*+?`$)(^%~\'.:;№=]/;

    !searchInoutRegex.test(event.target.value) &&
      setSearchValue(event.target.value);
  };

  return (
    <div
      className={cn(
        css.section,
        withBottomMargin && css.withBottomMargin,
        withLabel && css.withLabel
      )}
    >
      {withLabel && (
        <div className={css.title}>
          <Text
            fontSize={Text.FONT_SIZE.SIZE_32}
            fontWeight={Text.FONT_WEIGHT.MEDIUM}
            fontFamily={Text.FONT_FAMILY.RUBIK}
            lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_4}
            tagComponent={Text.TAG.H1}
          >
            {searchLabel ? (
              searchLabel
            ) : (
              <TextLocal
                id={searchHeaderText}
                params={{
                  fullName: `${query[SEARCH_FILTERS.FIRST_NAME]} ${
                    query[SEARCH_FILTERS.LAST_NAME] === 'undefined'
                      ? ''
                      : query[SEARCH_FILTERS.LAST_NAME]
                  }`,
                }}
              />
            )}
          </Text>
        </div>
      )}
      <div className={css.searchFieldContainer}>
        <input
          type="search"
          placeholder={t('search.searchPlaceholder')}
          className={css.searchInput}
          value={searchValue}
          onChange={onSearchInputChangeHandler}
        />
      </div>
    </div>
  );
};

SearchFieldContainer.propTypes = {
  withBottomMargin: PropTypes.bool,
  searchLabel: PropTypes.string,
  withLabel: PropTypes.bool,
};

SearchFieldContainer.defaultProps = {
  withBottomMargin: false,
  searchLabel: null,
  withLabel: true,
};

export default memo(SearchFieldContainer);
